import { TRegionKey } from "../models/region-model";
import { TFeatureSwitch } from "./features";

type Keys<T extends readonly string[]> = T[number];

const apiEndpoints = {
  MatterManagementGraphQLUrl: "/mattermanagement/browse-graphql/graphql",
  MicrosoftAuthRedirectUrl:
    "/itops/integration/microsoft/authredirect/authentication/GetAuthorizationRequest?scope=appFiles",
  MatterAssignUrl: "/mattermanagement/MailSync/Matter/Assign",
  MatterUnAssignUrl: "/mattermanagement/MailSync/Matter/UnAssign",
  FileDownloadUrls: "/mattermanagement/MailSync/File/DownloadUrls",
  AddMetricUrl: "/itops/metrics/api/AddMetric",
} as const;

export type TApiEndpointKeys = keyof typeof apiEndpoints;
const API_ENDPOINT_KEYS = Object.keys(apiEndpoints) as TApiEndpointKeys[];
const CONFIG_KEYS = [
  "CognitoClientId",
  "CognitoUrl",
  "MvcBaseUrl",
  "ApiUrl",
  "WebappUrl",
  "SignUpUrl",
  ...API_ENDPOINT_KEYS,
] as const;
type TConfigKeys = Keys<typeof CONFIG_KEYS>;

export type TConfig = Record<TConfigKeys, string> & { Features?: TFeatureSwitch[] };
type TBaseConfig = Omit<TConfig, TApiEndpointKeys>;

type TRegions = Record<TRegionKey, TConfig>;

const ENVIRONMENTS = ["Dev", "Staging", "Production"] as const;
export type TEnvironmentKey = Keys<typeof ENVIRONMENTS>;
type TEnvironments = Record<TEnvironmentKey, TRegions>;

const PRODUCTS = ["Smokeball"] as const;
export type TProductKey = Keys<typeof PRODUCTS>;
type TProducts = Record<TProductKey, TEnvironments>;

const makeConfig = (baseConfig: TBaseConfig): TConfig => {
  const config = { ...baseConfig } as TConfig;

  for (const k of API_ENDPOINT_KEYS) {
    config[k] = `${baseConfig.ApiUrl}${apiEndpoints[k]}`;
  }

  return config;
};

const allConfigs: TProducts = {
  Smokeball: {
    Dev: {
      AUS: makeConfig({
        CognitoClientId: "2fujo5dpe9r5n1okl10krbfnd7",
        CognitoUrl: "https://devauth.smokeball.com.au",
        MvcBaseUrl: "https://devtoolbar.smokeball.com.au",
        ApiUrl: "https://devappgw.smokeball.com.au",
        WebappUrl: "https://dev-app.smokeball.com.au",
        SignUpUrl: "https://www.smokeball.com.au/pricing",
        Features: [],
      }),
      USA: makeConfig({
        CognitoClientId: "6nhhkm1ff26ah0g694u6kpfqqf",
        CognitoUrl: "https://devauth.smokeball.com",
        MvcBaseUrl: "https://devtoolbar.smokeball.com",
        ApiUrl: "https://devappgw.smokeball.com",
        WebappUrl: "https://dev-app.smokeball.com",
        SignUpUrl: "https://www.smokeball.com/pricing",
      }),
      GB: makeConfig({
        CognitoClientId: "19fuih1numq3vlnp23j04b64i2",
        CognitoUrl: "https://devauth.smokeball.co.uk",
        MvcBaseUrl: "https://devtoolbar.smokeball.co.uk",
        ApiUrl: "https://devappgw.smokeball.co.uk",
        WebappUrl: "https://dev-app.smokeball.co.uk",
        SignUpUrl: "https://www.smokeball.co.uk/pricing",
      }),
    },
    Staging: {
      AUS: makeConfig({
        CognitoClientId: "27rm09hgfsinmc75c94jh63im7",
        CognitoUrl: "https://datastaging-auth.smokeball.com.au",
        MvcBaseUrl: "https://staging.smokeball.com.au",
        ApiUrl: "https://stagingappgw.smokeball.com.au",
        WebappUrl: "https://rc-app.smokeball.com.au",
        SignUpUrl: "https://www.smokeball.com.au/pricing",
      }),
      USA: makeConfig({
        CognitoClientId: "37tvi91gn7umqua2k7lv1us25d",
        CognitoUrl: "https://datastaging-auth.smokeball.com",
        MvcBaseUrl: "https://staging.smokeball.com",
        ApiUrl: "https://stagingappgw.smokeball.com",
        WebappUrl: "https://rc-app.smokeball.com",
        SignUpUrl: "https://www.smokeball.com/pricing",
      }),
      GB: makeConfig({
        CognitoClientId: "4ce51crvpu738hie4n13g4c7on",
        CognitoUrl: "https://datastaging-auth.smokeball.co.uk",
        MvcBaseUrl: "https://staging.smokeball.co.uk",
        ApiUrl: "https://stagingappgw.smokeball.co.uk",
        WebappUrl: "https://rc-app.smokeball.co.uk",
        SignUpUrl: "https://www.smokeball.co.uk/pricing",
      }),
    },
    Production: {
      AUS: makeConfig({
        CognitoClientId: "48uu68ihkud4l06p3k0qta41um",
        CognitoUrl: "https://auth.smokeball.com.au",
        MvcBaseUrl: "https://toolbar.smokeball.com.au",
        ApiUrl: "https://appgw.smokeball.com.au",
        WebappUrl: "https://app.smokeball.com.au",
        SignUpUrl: "https://www.smokeball.com.au/pricing",
      }),
      USA: makeConfig({
        CognitoClientId: "3usbtnbdk6so5295l37snd0am7",
        CognitoUrl: "https://auth.smokeball.com",
        MvcBaseUrl: "https://toolbar.smokeball.com",
        ApiUrl: "https://appgw.smokeball.com",
        WebappUrl: "https://app.smokeball.com",
        SignUpUrl: "https://www.smokeball.com/pricing",
      }),
      GB: makeConfig({
        CognitoClientId: "623tcihvadnsvp380anpm7l9ll",
        CognitoUrl: "https://auth.smokeball.co.uk",
        MvcBaseUrl: "https://toolbar.smokeball.co.uk",
        ApiUrl: "https://appgw.smokeball.co.uk",
        WebappUrl: "https://app.smokeball.co.uk",
        SignUpUrl: "https://www.smokeball.co.uk/pricing",
      }),
    },
  },
};

export const getConfig = (
  product: keyof TProducts,
  environment: keyof TEnvironments,
  region: keyof TRegions
): TConfig => ({ ...allConfigs[product][environment][region] });
